import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Clock3, Microscope, BadgeCheck, Search } from "lucide-react";
import { IMAGES } from "@/data/catalog";
import { useState } from "react";

const lineReveal = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({ y: "0%", opacity: 1, transition: { delay: 0.35 + i * 0.16, duration: 0.85, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero({ onBook, onSearch }) {
  const ref = useRef(null);
  const [query, setQuery] = useState("");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const go = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen bg-[#0B192C] dot-grid spotlight grain overflow-hidden pt-[70px] flex items-center" data-testid="hero-section">
      {/* ECG pulse across hero */}
      <svg className="absolute top-1/2 left-0 w-full opacity-[0.12] pointer-events-none" viewBox="0 0 1200 120" preserveAspectRatio="none">
        <path className="ecg-line" d="M0,60 L200,60 L230,60 L245,20 L260,95 L275,40 L290,60 L500,60 L530,60 L545,15 L562,100 L577,35 L592,60 L820,60 L850,60 L865,22 L880,95 L895,42 L910,60 L1200,60" stroke="#D92638" strokeWidth="2.5" fill="none" />
      </svg>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-1.5 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-pulse" />
            <span className="text-xs font-medium tracking-widest uppercase text-teal-100" data-testid="hero-opening-badge">Opening soon - MVP Colony, Visakhapatnam</span>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.4rem] font-extrabold text-white leading-[1.05] tracking-tight">
            <span className="reveal-line"><motion.span custom={0} variants={lineReveal} initial="hidden" animate="show">Blood tells stories.</motion.span></span>
            <span className="reveal-line"><motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="text-[#D92638]">We read them in</motion.span></span>
            <span className="reveal-line"><motion.span custom={2} variants={lineReveal} initial="hidden" animate="show"><span className="font-mono-med text-[#14B8A6]">24-48 hours.</span></motion.span></span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-7 text-base lg:text-lg text-slate-300 max-w-xl leading-relaxed">
            Vizag's only CMC Vellore-trained hematopathologist-led diagnostic center. Bone marrow, flow cytometry and complete diagnostics - reported in-house, same city, same specialist. No more couriering samples to Hyderabad and waiting a week.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={() => onBook([])} data-testid="hero-book-button"
              className="group flex items-center gap-2.5 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:-translate-y-1 active:scale-95 shadow-[0_10px_35px_-8px_rgba(217,38,56,0.6)]">
              Book Appointment
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a href="#tests" onClick={(e) => go(e, "#tests")} data-testid="hero-explore-tests"
              className="flex items-center gap-2.5 border border-white/25 hover:border-[#14B8A6] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:-translate-y-1 active:scale-95 backdrop-blur-sm hover:bg-white/5">
              <Microscope className="w-4 h-4 text-[#14B8A6]" /> Explore Tests
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-7 max-w-lg">
            <form onSubmit={(e) => { e.preventDefault(); onSearch?.(query); }} data-testid="hero-search-form"
              className="flex items-center gap-2 bg-white/95 backdrop-blur-xl rounded-full pl-5 pr-2 py-2 shadow-[0_10px_35px_-10px_rgba(0,0,0,0.5)] border border-white/20">
              <Search className="w-4.5 h-4.5 w-5 h-5 text-slate-400 shrink-0" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="hero-search-input"
                placeholder="Search a test or health checkup - CBC, thyroid, full body..."
                className="flex-1 bg-transparent text-sm text-[#0B192C] placeholder:text-slate-400 focus:outline-none" />
              <button type="submit" data-testid="hero-search-button"
                className="shrink-0 bg-[#0B192C] hover:bg-[#1E3E62] text-white text-xs font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95">
                Search
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-metrics">
            {[
              { v: "24-48h", l: "Hematopathology TAT" },
              { v: "CMC", l: "Vellore-trained specialist" },
              { v: "100%", l: "Reports specialist-signed" },
            ].map((m) => (
              <div key={m.l} className="border-l-2 border-[#D92638]/60 pl-4">
                <div className="font-mono-med text-xl lg:text-2xl font-semibold text-white">{m.v}</div>
                <div className="text-xs text-slate-400 mt-1 leading-snug">{m.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: clipped spotlight image */}
        <div className="lg:col-span-5 relative">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative">
            <motion.div style={{ y: imgY }} className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
              <img src={IMAGES.hero} alt="Hemozy laboratory - hematopathology" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/90 via-transparent to-[#0B192C]/20" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3">
                <Clock3 className="w-5 h-5 text-[#14B8A6] shrink-0" />
                <div>
                  <div className="text-white text-sm font-semibold">In-house hematopathology lab</div>
                  <div className="text-slate-300 text-xs">Flow cytometry · Bone marrow · Coagulation</div>
                </div>
              </div>
            </motion.div>

            <motion.div style={{ y: badgeY }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
              className="absolute -top-6 -right-2 lg:-right-6 bg-white rounded-2xl shadow-[0_20px_50px_-12px_rgba(11,25,44,0.4)] px-5 py-4 animate-float-slow" data-testid="hero-tat-widget">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Clock3 className="w-3.5 h-3.5 text-[#D92638]" /> Others courier out</div>
              <div className="font-mono-med text-lg font-semibold text-slate-400 line-through">3-7 days</div>
              <div className="mt-2 flex items-center gap-2 text-xs font-medium text-[#0D9488]"><BadgeCheck className="w-3.5 h-3.5" /> Hemozy, same city</div>
              <div className="font-mono-med text-2xl font-bold text-[#0B192C]">24-48 hrs</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
